// src/store/orders-store.ts
import { create } from "zustand";
import { persist, PersistStorage } from "zustand/middleware";
import { v4 as uuidv4 } from "uuid";
import { Order } from "../interfaces/domain/menu";

const CART_TTL = 2 * 60 * 60 * 1000;
const TIMESTAMP_KEY = "orders-storage-timestamp";

interface State {
  orders: Order[];
  canUseCart: boolean;

  addItem: (item: Omit<Order, "cartItemId">) => void;
  incrementItem: (cartItemId: string) => void;
  decrementItem: (cartItemId: string) => void;
  removeItem: (cartItemId: string) => void;
  updateItem: (cartItemId: string, updatedOrder: Order) => void;
  setCanUseCart: (value: boolean) => void;
  clear: () => void;
}

const storage: PersistStorage<{ orders: Order[] }> = {
  getItem: (name) => {
    const str = localStorage.getItem(name);
    return str ? JSON.parse(str) : null;
  },
  setItem: (name, value) => {
    localStorage.setItem(name, JSON.stringify(value));
    localStorage.setItem(TIMESTAMP_KEY, Date.now().toString());
  },
  removeItem: (name) => localStorage.removeItem(name),
};

export const useOrdersStore = create<State>()(
  persist(
    (set, get) => ({
      orders: [],
      canUseCart: false,

      addItem: (item) =>
        set({ orders: [...get().orders, { ...item, cartItemId: uuidv4() }] }),

      incrementItem: (cartItemId) =>
        set({
          orders: get().orders.map((o) =>
            o.cartItemId === cartItemId ? { ...o, quantity: o.quantity + 1 } : o
          ),
        }),

      decrementItem: (cartItemId) => {
        const item = get().orders.find((o) => o.cartItemId === cartItemId);
        if (!item) return;
        if (item.quantity > 1) {
          set({
            orders: get().orders.map((o) =>
              o.cartItemId === cartItemId ? { ...o, quantity: o.quantity - 1 } : o
            ),
          });
        } else {
          get().removeItem(cartItemId);
        }
      },

      removeItem: (cartItemId) =>
        set({ orders: get().orders.filter((o) => o.cartItemId !== cartItemId) }),

      updateItem: (cartItemId, updatedOrder) =>
        set({
          orders: get().orders.map((o) =>
            o.cartItemId === cartItemId ? { ...updatedOrder, cartItemId } : o
          ),
        }),

      setCanUseCart: (value) => set({ canUseCart: value }),
      clear: () => set({ orders: [] }),
    }),
    {
      name: "orders-storage",
      storage,
      partialize: (state) => ({ orders: state.orders }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const last = localStorage.getItem(TIMESTAMP_KEY);
        if (last && Date.now() - parseInt(last) > CART_TTL) {
          state.clear();
          localStorage.removeItem(TIMESTAMP_KEY);
        }
      },
    }
  )
);
